import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import { QRCodeCanvas } from "qrcode.react";
import {
  ArrowLeft, Play, Save, Eye, Sparkles, FilePlus, Trash2, Pencil,
  ChevronDown, X, Terminal as TermIcon, FileCode, Wand2, Smartphone, Copy, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, API } from "@/lib/api";
import AIPanel from "@/components/AIPanel";
import { getWs, setWs, starterFiles, categoryOf, clearWsByProjectId } from "@/lib/workspace";

const FOLDER_LABEL = { html: "HTML", python: "Python", java: "Java", javascript: "JavaScript", cpp: "C++", sql: "SQL", ml: "Machine Learning" };
const folderLabel = (lang) => FOLDER_LABEL[lang] || (lang || "").toUpperCase();

const LANG_BY_EXT = { html: "html", htm: "html", css: "css", js: "javascript", jsx: "javascript", ts: "typescript", py: "python", java: "java", sql: "sql", json: "json", md: "markdown", c: "c", cpp: "cpp", txt: "plaintext" };
const ext = (name) => name.split(".").pop().toLowerCase();
const langOf = (name) => LANG_BY_EXT[ext(name)] || "plaintext";

const RUN_LANG = { py: "python", js: "javascript", java: "java", cpp: "cpp", c: "c", sql: "sql" };

function buildPreview(files) {
  const html = files.find((f) => ext(f.name) === "html");
  if (!html) return "<h1 style='font-family:sans-serif;color:#00D4D4;background:#0A0A0A;padding:40px'>No index.html found</h1>";
  let doc = html.content;
  const css = files.filter((f) => ext(f.name) === "css").map((f) => f.content).join("\n");
  const js = files.filter((f) => ext(f.name) === "js").map((f) => f.content).join("\n");
  doc = doc.replace(/<link[^>]*rel=["']stylesheet["'][^>]*>/gi, "");
  doc = doc.replace(/<script[^>]*src=["'][^"']*["'][^>]*><\/script>/gi, "");
  if (css) doc = doc.includes("</head>") ? doc.replace("</head>", `<style>${css}</style></head>`) : `<style>${css}</style>` + doc;
  if (js) doc = doc.includes("</body>") ? doc.replace("</body>", `<script>${js}<\/script></body>`) : doc + `<script>${js}<\/script>`;
  return doc;
}

export default function CodeEditor() {
  const { id, language: langParam } = useParams();
  const isDraft = !!langParam;
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [active, setActive] = useState("");
  const [openTabs, setOpenTabs] = useState([]);
  const [output, setOutput] = useState("");
  const [errMode, setErrMode] = useState(false);
  const [running, setRunning] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiSeed, setAiSeed] = useState(null);
  const [preview, setPreview] = useState(null); // {url}
  const [newFileOpen, setNewFileOpen] = useState(false);
  const [newFileName, setNewFileName] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newProjName, setNewProjName] = useState("");
  const langRef = useRef(null);
  const editorRef = useRef(null);
  const initCursor = useRef(null);

  const writeWs = useCallback((patch) => {
    const lang = langRef.current;
    if (!lang) return;
    const prev = getWs(lang) || {};
    setWs(lang, { ...prev, ...patch });
  }, []);

  const applyWs = useCallback((ws) => {
    setProject({ id: ws.projectId || null, name: ws.name, category: ws.category, language: ws.language });
    setFiles(ws.files || []);
    const first = ws.active || ws.files?.[0]?.name || "";
    setActive(first);
    setOpenTabs(ws.openTabs?.length ? ws.openTabs : (first ? [first] : []));
    initCursor.current = { line: ws.cursor?.line || 1, col: ws.cursor?.col || 1, scroll: ws.scroll || 0 };
  }, []);

  const load = useCallback(async () => {
    if (isDraft) {
      const lang = langParam;
      langRef.current = lang;
      let ws = getWs(lang);
      if (!ws || ws.projectId) {
        const sf = starterFiles(lang);
        ws = { projectId: null, name: `${folderLabel(lang)} Project`, language: lang, category: categoryOf(lang),
               files: sf, active: sf[0].name, openTabs: [sf[0].name], cursor: { line: 1, col: 1 }, scroll: 0, dirty: true };
        setWs(lang, ws);
      }
      applyWs(ws);
      return;
    }
    try {
      const { data } = await api.get(`/projects/${id}`);
      const lang = data.language;
      langRef.current = lang;
      const ws = getWs(lang);
      if (ws && ws.projectId === id) {
        applyWs(ws); // restore unsaved edits + cursor for the same project
      } else {
        const newWs = { projectId: id, name: data.name, language: lang, category: data.category,
          files: data.files || [], active: data.files?.[0]?.name || "",
          openTabs: data.files?.[0] ? [data.files[0].name] : [], cursor: { line: 1, col: 1 }, scroll: 0, dirty: false };
        setWs(lang, newWs); applyWs(newWs);
      }
    } catch (e) {
      // Stale workspace referencing a deleted/foreign project — purge it and recover cleanly.
      clearWsByProjectId(id);
      toast.error(apiErr(e));
      nav("/coding");
    }
  }, [id, langParam, isDraft, applyWs, writeWs, nav]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { if (langRef.current && active) writeWs({ active, openTabs }); }, [active, openTabs, writeWs]);

  const updateContent = (name, content) => {
    setFiles((prev) => { const nf = prev.map((f) => f.name === name ? { ...f, content } : f); writeWs({ files: nf, dirty: true }); return nf; });
  };

  const openFile = (name) => { setActive(name); setOpenTabs((t) => t.includes(name) ? t : [...t, name]); };
  const closeTab = (name) => {
    setOpenTabs((t) => { const nt = t.filter((x) => x !== name); if (active === name) setActive(nt[nt.length - 1] || ""); return nt; });
  };

  const addFile = () => {
    const name = newFileName.trim();
    if (!name) return;
    if (files.find((f) => f.name === name)) return toast.error("File already exists");
    const nf = [...files, { name, content: "" }];
    setFiles(nf); writeWs({ files: nf, dirty: true }); openFile(name); setNewFileOpen(false); setNewFileName("");
  };
  const deleteFile = (name) => {
    if (files.length === 1) return toast.error("At least one file required");
    if (!window.confirm(`Delete ${name}?`)) return;
    const nf = files.filter((f) => f.name !== name);
    setFiles(nf); writeWs({ files: nf, dirty: true }); closeTab(name);
  };
  const renameFile = (oldName) => {
    const nn = window.prompt("New file name", oldName);
    if (!nn || nn === oldName) return;
    const nf = files.map((f) => f.name === oldName ? { ...f, name: nn } : f);
    setFiles(nf); writeWs({ files: nf, dirty: true });
    setOpenTabs((t) => t.map((x) => x === oldName ? nn : x));
    if (active === oldName) setActive(nn);
  };

  const save = async () => {
    const lang = langRef.current;
    let pid = project.id;
    try {
      if (!pid) {
        const { data } = await api.post("/projects", { name: project.name, category: project.category, language: lang, files });
        pid = data.id; setProject((p) => ({ ...p, id: pid }));
        nav(`/editor/${pid}`, { replace: true });
      } else {
        await api.put(`/projects/${pid}`, { name: project.name, files });
      }
      writeWs({ projectId: pid, files, dirty: false });
      toast.success(`Project '${project.name}' successfully saved.`);
      setNewProjName(`${folderLabel(lang)} Project`);
      setCreateOpen(true);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const createNewProject = () => {
    const name = newProjName.trim();
    if (!name) return toast.error("Enter a project name");
    const lang = langRef.current;
    const sf = starterFiles(lang);
    const ws = { projectId: null, name, language: lang, category: categoryOf(lang),
      files: sf, active: sf[0].name, openTabs: [sf[0].name], cursor: { line: 1, col: 1 }, scroll: 0, dirty: true };
    setWs(lang, ws);
    setCreateOpen(false); setPreview(null); setOutput(""); setErrMode(false);
    window.history.replaceState(null, "", `/editor/new/${lang}`);
    applyWs(ws);
  };

  const doPreview = async () => {
    try {
      const html = buildPreview(files);
      const { data } = await api.post("/preview", { html });
      setPreview({ url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const copyPreviewUrl = async () => {
    try { await navigator.clipboard.writeText(preview.url); toast.success("Preview URL copied"); }
    catch { toast.error("Could not copy URL"); }
  };

  const handleMount = (editor, monaco) => {
    editorRef.current = editor;
    // Disable Monaco's Ctrl/Cmd+S (browser Save As) — only in-app Save is allowed
    try { editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => { toast.info("Use the in-app Save button to save your project."); }); } catch {}
    const c = initCursor.current;
    if (c) {
      initCursor.current = null; // apply restoration only once (on project open)
      try { editor.setPosition({ lineNumber: c.line, column: c.col }); editor.revealLineInCenter(c.line); if (c.scroll) editor.setScrollTop(c.scroll); } catch {}
    }
    editor.onDidChangeCursorPosition((e) => writeWs({ cursor: { line: e.position.lineNumber, col: e.position.column } }));
    editor.onDidScrollChange((e) => writeWs({ scroll: e.scrollTop }));
  };

  // Block browser Ctrl/Cmd+S (Save As / download) anywhere in the editor page
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && ["s", "u"].includes(e.key.toLowerCase())) {
        e.preventDefault(); e.stopPropagation();
        toast.info("Saving outside KiqHub is disabled. Use the in-app Save button.");
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, []);

  const run = async () => {
    setErrMode(false);
    if (project.category === "frontend" && files.some((f) => ext(f.name) === "html")) {
      return doPreview();
    }
    const runnable = RUN_LANG[ext(active)] ? active : files.find((f) => RUN_LANG[ext(f.name)])?.name;
    if (!runnable) { setOutput("No runnable file (.py .js .java .cpp .sql) in this project."); return; }
    const lang = RUN_LANG[ext(runnable)];
    setRunning(true); setOutput("Running…");
    try {
      const { data } = await api.post("/execute", { language: lang, project_id: project.id || `draft-${langRef.current}`, files: files.map((f) => ({ name: f.name, content: f.content })) });
      const out = (data.output || "") + (data.stderr ? `\n${data.stderr}` : "");
      setOutput(out.trim() || "(no output)");
      if (data.stderr || data.code !== 0) setErrMode(true);
    } catch (e) { setOutput(apiErr(e)); setErrMode(true); } finally { setRunning(false); }
  };

  const applyAIFiles = (aiFiles) => {
    setFiles((prev) => {
      const map = Object.fromEntries(prev.map((f) => [f.name, f]));
      aiFiles.forEach((f) => { map[f.name] = { name: f.name, content: f.content }; });
      const nf = Object.values(map);
      writeWs({ files: nf, dirty: true });
      if (aiFiles[0]) openFile(aiFiles[0].name);
      return nf;
    });
  };

  const tryWithAI = (fix) => {
    setAiOpen(true);
    const f = files.find((x) => x.name === active);
    if (fix) {
      setAiSeed(`I ran my code and got an error. Please FIX it directly and return the corrected file(s).\n\nFile: ${active}\n\`\`\`\n${f?.content || ""}\n\`\`\`\n\nError:\n\`\`\`\n${output}\n\`\`\``);
    } else {
      setAiSeed(`Explain this error in detail — the exact line number and what is wrong — but DO NOT change any code.\n\nFile: ${active}\n\`\`\`\n${f?.content || ""}\n\`\`\`\n\nError:\n\`\`\`\n${output}\n\`\`\``);
    }
  };

  if (!project) return <div className="min-h-screen flex items-center justify-center text-teal-accent">Loading…</div>;
  const activeFile = files.find((f) => f.name === active);
  const contextStr = `Project "${project.name}" (${project.category}/${project.language}). Files: ${files.map((f) => f.name).join(", ")}.\n\n` +
    files.map((f) => `--- ${f.name} ---\n${f.content}`).join("\n\n");

  return (
    <div className="h-screen flex flex-col bg-[#0A0A0A] overflow-hidden">
      {/* Toolbar */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-border/60 glass-strong shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button data-testid="editor-back" onClick={() => nav("/coding")} className="p-2 rounded-lg hover:bg-white/5"><ArrowLeft size={18} /></button>
          <span className="font-semibold truncate">{project.name}</span>
          <span className="text-[10px] pill px-2 py-0.5 bg-[#00D4D4]/10 text-teal-accent uppercase">{project.language}</span>
        </div>
        <div className="flex items-center gap-2">
          <ToolBtn onClick={() => setAiOpen((o) => !o)} icon={Sparkles} label="AI" active={aiOpen} testid="editor-ai-btn" />
          {project.category === "frontend" && files.some((f) => ext(f.name) === "html") && (
            <ToolBtn onClick={doPreview} icon={Eye} label="Preview" testid="editor-preview-btn" />
          )}
          <ToolBtn onClick={run} icon={Play} label={running ? "…" : "Run"} primary testid="editor-run-btn" />
          <ToolBtn onClick={save} icon={Save} label="Save" testid="editor-save-btn" />
        </div>
      </div>

      <PanelGroup direction="horizontal" className="flex-1">
        {/* Explorer */}
        <Panel defaultSize={18} minSize={12} className="glass-strong">
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-3 py-3 border-b border-border/60">
              <span className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2"><FileCode size={14} /> Explorer</span>
              <button data-testid="new-file-btn" onClick={() => setNewFileOpen(true)} className="p-1.5 rounded hover:bg-white/5 text-teal-accent"><FilePlus size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              <div className="px-3 text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{project.name}</div>
              {files.map((f) => (
                <div key={f.name} data-testid={`file-${f.name}`}
                     className={`group flex items-center justify-between px-3 py-1.5 cursor-pointer text-sm ${active === f.name ? "bg-[#00D4D4]/10 text-teal-accent" : "hover:bg-white/5"}`}
                     onClick={() => openFile(f.name)}>
                  <span className="truncate flex items-center gap-2"><FileCode size={14} className="opacity-70" /> {f.name}</span>
                  <span className="hidden group-hover:flex items-center gap-1">
                    <button onClick={(e) => { e.stopPropagation(); renameFile(f.name); }} className="text-muted-foreground hover:text-foreground"><Pencil size={12} /></button>
                    <button onClick={(e) => { e.stopPropagation(); deleteFile(f.name); }} className="text-red-400"><Trash2 size={12} /></button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Panel>
        <PanelResizeHandle className="w-1.5 bg-transparent hover:bg-[#00D4D4]/30 transition-colors" />

        {/* Editor + Terminal */}
        <Panel minSize={30}>
          <PanelGroup direction="vertical">
            <Panel minSize={20}>
              <div className="h-full flex flex-col">
                <div className="flex items-center border-b border-border/60 overflow-x-auto shrink-0">
                  {openTabs.map((t) => (
                    <div key={t} onClick={() => setActive(t)}
                         className={`flex items-center gap-2 px-4 py-2.5 text-sm cursor-pointer border-r border-border/40 whitespace-nowrap ${active === t ? "bg-[#00D4D4]/8 text-teal-accent" : "text-muted-foreground hover:text-foreground"}`}>
                      {t}
                      <button onClick={(e) => { e.stopPropagation(); closeTab(t); }} className="hover:text-foreground"><X size={13} /></button>
                    </div>
                  ))}
                </div>
                <div className="flex-1">
                  {activeFile ? (
                    <Editor height="100%" theme="vs-dark" path={activeFile.name}
                            language={langOf(activeFile.name)} value={activeFile.content}
                            onMount={handleMount}
                            onChange={(v) => updateContent(activeFile.name, v ?? "")}
                            options={{ fontSize: 14, minimap: { enabled: false }, fontLigatures: true, scrollBeyondLastLine: false, padding: { top: 12 }, tabSize: 2, folding: true, lineNumbers: "on" }} />
                  ) : <div className="h-full flex items-center justify-center text-muted-foreground text-sm">Open a file to start editing</div>}
                </div>
              </div>
            </Panel>
            <PanelResizeHandle className="h-1.5 bg-transparent hover:bg-[#00D4D4]/30 transition-colors" />
            <Panel defaultSize={30} minSize={8} className="glass-strong">
              <div className="h-full flex flex-col">
                <div className="flex items-center justify-between px-4 py-2 border-b border-border/60 shrink-0">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2"><TermIcon size={14} /> Output</span>
                  <ChevronDown size={16} className="text-muted-foreground" />
                </div>
                <div className="flex-1 overflow-y-auto p-4 font-mono text-sm whitespace-pre-wrap" data-testid="editor-output">
                  <span className={errMode ? "text-red-400" : "text-[#8fe]"}>{output || "Press Run to execute your code. Output appears here."}</span>
                  {errMode && (
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-muted-foreground">Try with AI —</span>
                      <button data-testid="try-with-ai-fix" onClick={() => tryWithAI(true)}
                              className="kiq-btn px-4 py-2 text-xs inline-flex items-center gap-2"><Wand2 size={14} /> Yes, fix it</button>
                      <button data-testid="try-with-ai-explain" onClick={() => tryWithAI(false)}
                              className="pill px-4 py-2 text-xs inline-flex items-center gap-2 glass hover:border-[#00D4D4]/50">No, just explain</button>
                    </div>
                  )}
                </div>
              </div>
            </Panel>
          </PanelGroup>
        </Panel>

        {/* AI panel */}
        {aiOpen && (
          <>
            <PanelResizeHandle className="w-1.5 bg-transparent hover:bg-[#00D4D4]/30 transition-colors" />
            <Panel defaultSize={28} minSize={18} className="relative">
              <AIPanel context={contextStr} mode="coding" sessionId={`proj-${project.id || langRef.current}`} onApplyFiles={applyAIFiles} seedPrompt={aiSeed} onClose={() => { setAiOpen(false); setAiSeed(null); }} />
            </Panel>
          </>
        )}

        {/* Live Preview panel (resizable) */}
        {preview && (
          <>
            <PanelResizeHandle className="w-1.5 bg-transparent hover:bg-[#00D4D4]/30 transition-colors" />
            <Panel defaultSize={38} minSize={20} className="glass-strong" data-testid="preview-panel">
              <div className="h-full flex flex-col">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60 shrink-0">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2"><Eye size={14} className="text-teal-accent" /> Preview</span>
                  <div className="flex items-center gap-1.5">
                    <div className="hidden lg:flex items-center gap-1.5 glass rounded-lg px-2 py-1 mr-1">
                      <Smartphone size={12} className="text-teal-accent" />
                      <QRCodeCanvas value={preview.url} size={26} bgColor="transparent" fgColor="#00D4D4" />
                    </div>
                    <button data-testid="preview-copy-url" onClick={copyPreviewUrl} title="Copy URL"
                            className="p-1.5 rounded-lg glass hover:border-[#00D4D4]/40 text-teal-accent flex items-center gap-1.5 text-xs px-2.5"><Copy size={14} /> Copy URL</button>
                    <button data-testid="preview-refresh" onClick={doPreview} title="Refresh" className="p-1.5 rounded-lg hover:bg-white/5"><RefreshCw size={15} /></button>
                    <button data-testid="preview-close" onClick={() => setPreview(null)} title="Close" className="p-1.5 rounded-lg hover:bg-white/5"><X size={16} /></button>
                  </div>
                </div>
                <iframe title="preview" src={preview.url} className="flex-1 w-full bg-white" />
              </div>
            </Panel>
          </>
        )}
      </PanelGroup>

      {/* status bar */}
      <div className="h-7 flex items-center justify-between px-4 text-[11px] text-muted-foreground border-t border-border/60 glass-strong shrink-0">
        <span className="uppercase">{project.language}</span>
        <span>{files.length} files</span>
      </div>

      {/* New file modal */}
      {newFileOpen && (
        <Modal onClose={() => setNewFileOpen(false)} title="New file">
          <input data-testid="new-file-input" value={newFileName} onChange={(e) => setNewFileName(e.target.value)}
                 placeholder="e.g. app.js, style.css, query.sql" autoFocus
                 onKeyDown={(e) => e.key === "Enter" && addFile()}
                 className="kiq-input w-full px-4 py-3 text-sm mb-4" />
          <div className="flex gap-3">
            <button onClick={() => setNewFileOpen(false)} className="flex-1 py-2.5 rounded-full glass text-sm">Cancel</button>
            <button data-testid="new-file-create" onClick={addFile} className="flex-1 kiq-btn py-2.5 text-sm">Create</button>
          </div>
        </Modal>
      )}

      {/* Post-save: create a new project */}
      {createOpen && (
        <Modal onClose={() => setCreateOpen(false)} title="Now create a new project">
          <p className="text-xs text-muted-foreground mb-4">{folderLabel(project.language)} · rename &amp; save, or cancel to keep editing</p>
          <input data-testid="editor-new-project-name" value={newProjName} onChange={(e) => setNewProjName(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && createNewProject()} autoFocus
                 className="kiq-input w-full px-4 py-3 text-sm mb-4" />
          <div className="flex gap-3">
            <button onClick={() => setCreateOpen(false)} className="flex-1 py-2.5 rounded-full glass text-sm">Cancel</button>
            <button data-testid="editor-new-project-save" onClick={createNewProject} className="flex-1 kiq-btn py-2.5 text-sm">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// status bar helper components
function ToolBtn({ onClick, icon: Icon, label, primary, active, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
            className={`px-3.5 py-1.5 rounded-full text-sm flex items-center gap-2 transition-all ${
              primary ? "kiq-btn" : active ? "bg-[#00D4D4]/15 text-teal-accent" : "glass hover:border-[#00D4D4]/40 text-foreground"}`}>
      <Icon size={15} /> {label}
    </button>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="glass-strong rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="serif text-xl mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}
